import { IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { createApp, h } from 'vue';
import App from '@/presentation/app.vue';
import store from './store';
import { ProfileDataCheckerPlugin } from './store/plugins/profile-data-checker-plugin';
import { ContentSwitcherPlugin } from './store/plugins/content-switcher-plugin';
import { TSnowplow } from './interfaces/external-services.interfaces';
import GlobalsPlugin from './utils/vue-globals';
import { AccountPageApplication } from './application/account-page-application';
import { AccountPageReadyEvent } from '../interfaces/events/account-page-ready-event';
import { RenderViewCommand } from '../interfaces/commands/render-view-command';
import { DecisionMakingServicePlugin } from './store/plugins/decision-making-service-plugin';
import { router } from '@/router/router';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';

import { EligibilityUpdateEvent } from '@studyportals/eligibility-model';
import { StudentField } from '@studyportals/studentdomain';
import { AnonymousStudentServiceReady } from '@studyportals/anonymous-student-interfaces';
import globals from './utils/globals';
import { LoginStatusCheckerPlugin } from './store/plugins/login-status-checker-plugin';
import { WishlistSyncPlugin } from './store/plugins/wishlist-plugin';

declare global {
	interface Window {
		rollbar: any;
		rollbarIgnore: (...args) => boolean;
		EventAggregationService: IEventAggregationService;
		snowplow?: TSnowplow;
		Shared: {
			breakpoints: (breakpoints: string[]) => boolean;
		};
	}
}

class Main implements ISubscriber<EligibilityUpdateEvent | AnonymousStudentServiceReady> {
	private readonly eventAggregationService: IEventAggregationService = window.EventAggregationService;

	public initialize(): void {
		this.registerEventAggregationService();
		this.executePlugins();

		this.eventAggregationService.subscribeTo(EligibilityUpdateEvent.EventType, this, true);
		this.eventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);

		const application = this.createAccountPageApplication();
		this.publishAccountPageReadyEvent(application);
	}

	private registerEventAggregationService(): void {
		const eventAggregationService = window.EventAggregationService;
		globals.eventAggregationService = eventAggregationService;
	}

	private executePlugins(): void {
		void new LoginStatusCheckerPlugin().initialize();
		void new WishlistSyncPlugin().initiate();
		void new ProfileDataCheckerPlugin(store).initiate();
		void new ContentSwitcherPlugin().initiate();
		void new DecisionMakingServicePlugin().initiate();
	}

	public async notify(event: EligibilityUpdateEvent | AnonymousStudentServiceReady): Promise<void> {
		if (event.eventType === EligibilityUpdateEvent.EventType) {
			store.mutations.setScholarshipEligibility((event as EligibilityUpdateEvent).currentRequirements);
			return;
		}

		const studentData = await (event as AnonymousStudentServiceReady).anonymousStudentService?.getStudentData([
			StudentField.FLAGS,
		]);

		if (!studentData || !studentData.flags) return;

		const flagsSetWhichAreScholarship = Object.keys(studentData.flags).reduce(
			(acc, key) => {
				if (key.startsWith('scholarship_')) {
					acc[key] = studentData.flags![key];
				}
				return acc;
			},
			{} as { [key: string]: number | boolean },
		);

		store.mutations.setScholarshipEligibilityItemsFlags(flagsSetWhichAreScholarship);
	}

	private createAccountPageApplication(): AccountPageApplication {
		const application = new AccountPageApplication();
		window['AccountPageApplication'] = application;

		return application;
	}

	private publishAccountPageReadyEvent(application: AccountPageApplication): void {
		this.eventAggregationService.publishTo(AccountPageReadyEvent.EventType, new AccountPageReadyEvent(application));
	}

	public renderPage(command: RenderViewCommand): void {
		if (!command?.detail?.placeholderElement) {
			console.warn('No placeholder element found. Cannot render page.');
			return;
		}

		const props = {
			viewType: command.detail.viewType,
		};

		const app = createApp({
			render: () => {
				return h(App, props);
			},
		});

		app.use(GlobalsPlugin);
		app.use(router);
		app.mount(command.detail.placeholderElement);
	}
}

const main = new Main();
export default main;
main.initialize();
